/**
 * 标准配合刚性组解析。
 *
 * 文件职责：
 * 1. 在 StandardMateComponentFreedom 已识别的“刚性连接边”之上，用并查集归并出完整的浮动刚性组；
 * 2. 为求解器投影收敛提供“把已证明相对自由度为 0 的子块当作单一刚体”的 plain-data 结果；
 * 3. 不重复实现刚性边判定，边检测唯一实现仍在 StandardMateComponentFreedom。
 *
 * 设计动机：
 * 多零件连续配合里，若一个刚性子块（例如两两之间都有 2 条独立同轴心 + 重合的电机与连杆）没有被当作单一刚体，
 * 相邻零件对它的多条同轴心就会在逐条投影里形成伪闭环，导致 Gauss-Seidel 式投影在极限环里振荡不收敛。
 * 把刚性子块归并成一个刚体后，伪闭环退化为树，投影可稳定收敛。
 */

import { AssemblyMateType, type AssemblyMateDefinition } from '../definition';
import {
  areAxesCoincident,
  resolveFloatingRigidComponentEdges,
} from './StandardMateComponentFreedom';
import { resolveAxisGeometry, type AxisGeometry } from './geometry/StandardMateGeometryAccess';
import type { StandardMateConstraintTolerance } from './StandardMateConstraintResidual';
import type { StandardMateSolverInput } from './StandardMateSolverTypes';

/**
 * 标准配合刚性组解析结果。
 */
export interface StandardMateRigidComponentGroups {
  /** 组件实例 ID 到刚性组代表 ID（并查集根）的映射；未参与刚性组的组件不在表内。 */
  readonly groupRootByComponentId: ReadonlyMap<string, string>;
  /** 刚性组代表 ID 到该组全部成员组件实例 ID 的映射（仅包含成员数 ≥ 2 的组）。 */
  readonly membersByGroupRoot: ReadonlyMap<string, readonly string[]>;
  /** 两端都落在同一刚性组内部的 Mate ID 集合（刚体内部约束，投影阶段应跳过）。 */
  readonly internalMateIds: ReadonlySet<string>;
  /**
   * 冗余同轴心 Mate ID 集合（投影阶段应跳过，仅参与最终残差校验）。
   *
   * 归并刚性组后，同一对“超节点”（组件或刚性组）之间可能存在多条轴线共线的同轴心，它们彼此冗余。
   * 逐条投影会因各条同轴心的有向翻转/绕不同轴原点旋转而互相拉扯形成极限环，这里保留每对超节点的第一条，
   * 其余标记为冗余跳过；由于共线，被跳过的同轴心在几何上仍然满足，最终校验不受影响。
   */
  readonly redundantMateIds: ReadonlySet<string>;
}

/**
 * 并查集：查找组件当前所属根，并做路径压缩。
 *
 * @param parents 并查集父指针表。
 * @param componentId 组件实例 ID。
 * @returns 组件所属集合的根 ID。
 */
function findUnionRoot(parents: Map<string, string>, componentId: string): string {
  let root = componentId;
  while (parents.get(root) !== root) {
    const next = parents.get(root);
    if (next === undefined) {
      parents.set(root, root);
      return root;
    }
    root = next;
  }
  // 路径压缩，避免深链重复查找。
  let cursor = componentId;
  while (cursor !== root) {
    const next = parents.get(cursor) ?? root;
    parents.set(cursor, root);
    cursor = next;
  }
  return root;
}

/**
 * 合并两个组件所属集合。
 *
 * @param parents 并查集父指针表。
 * @param firstComponentId 第一组件实例 ID。
 * @param secondComponentId 第二组件实例 ID。
 */
function unionComponents(parents: Map<string, string>, firstComponentId: string, secondComponentId: string): void {
  if (!parents.has(firstComponentId)) {
    parents.set(firstComponentId, firstComponentId);
  }
  if (!parents.has(secondComponentId)) {
    parents.set(secondComponentId, secondComponentId);
  }
  const firstRoot = findUnionRoot(parents, firstComponentId);
  const secondRoot = findUnionRoot(parents, secondComponentId);
  if (firstRoot !== secondRoot) {
    parents.set(secondRoot, firstRoot);
  }
}

/**
 * 从当前 world-space 配合图解析全部浮动刚性组。
 *
 * 设计说明：
 * 1. 先用 StandardMateComponentFreedom 的刚性边构建并查集，把多条边连通的组件合并成同一刚性组；
 * 2. 只返回成员数 ≥ 2 的刚性组，单个组件不构成需要联动的刚体；
 * 3. `internalMateIds` 收集两端都在同一刚性组内的全部 Mate（包含支撑边与其它内部约束），
 *    供求解器投影阶段跳过——刚体内部约束在联动写入下天然满足，逐条投影只会引入数值扰动。
 *
 * 设计约束：
 * 只有“全浮动且不含交互驱动组件”的刚性组才会被归并联动。若某个刚性子块里出现固定组件或本次被用户直接
 * 驱动的组件，它的位姿由固定/驱动锚点单独定义，不能被同组其它成员的联动 delta 带走，这类组通过
 * `excludedComponentIds` 被整体排除，交回既有求解流程处理。
 *
 * @param input 标准配合求解器输入。
 * @param currentMates 已变换到当前 world space 的 Mate 定义。
 * @param tolerance 几何容差。
 * @param excludedComponentIds 不参与刚性组联动的组件（固定组件 ∪ 交互驱动组件）。
 * @returns 浮动刚性组解析结果。
 */
export function resolveStandardMateRigidComponentGroups(
  input: StandardMateSolverInput,
  currentMates: readonly AssemblyMateDefinition[],
  tolerance: StandardMateConstraintTolerance,
  excludedComponentIds: ReadonlySet<string> = new Set<string>(),
): StandardMateRigidComponentGroups {
  const edges = resolveFloatingRigidComponentEdges(input, currentMates, tolerance);
  const parents = new Map<string, string>();
  edges.forEach((edge) => {
    unionComponents(parents, edge.firstComponentId, edge.secondComponentId);
  });

  // 归并每个根下的成员。
  const mutableMembersByRoot = new Map<string, string[]>();
  parents.forEach((_parent, componentId) => {
    const root = findUnionRoot(parents, componentId);
    const members = mutableMembersByRoot.get(root) ?? [];
    members.push(componentId);
    mutableMembersByRoot.set(root, members);
  });

  const groupRootByComponentId = new Map<string, string>();
  const membersByGroupRoot = new Map<string, readonly string[]>();
  mutableMembersByRoot.forEach((members, root) => {
    if (members.length < 2) {
      return;
    }
    // 含固定或驱动锚点的刚性块位姿由锚点单独定义，不纳入联动归并，避免联动 delta 带走锚点。
    if (members.some((componentId) => excludedComponentIds.has(componentId))) {
      return;
    }
    membersByGroupRoot.set(root, members);
    members.forEach((componentId) => {
      groupRootByComponentId.set(componentId, root);
    });
  });

  // 把组件映射到其所属超节点（刚性组根或组件自身），用于跨超节点的边归并。
  const resolveSuperNodeId = (componentInstanceId: string): string => (
    groupRootByComponentId.get(componentInstanceId) ?? componentInstanceId
  );
  // 无向超节点对 key。
  const resolveSuperNodePairKey = (firstId: string, secondId: string): string => (
    [firstId, secondId].sort().join('::')
  );

  // 收集两端都落在同一刚性组内部的 Mate。
  const internalMateIds = new Set<string>();
  currentMates.forEach((mate) => {
    const firstRoot = groupRootByComponentId.get(mate.firstEntity.componentInstanceId);
    const secondRoot = groupRootByComponentId.get(mate.secondEntity.componentInstanceId);
    if (firstRoot !== undefined && firstRoot === secondRoot) {
      internalMateIds.add(mate.mateId);
    }
  });

  /**
   * 检测冗余同轴心：按“超节点对”分组，同一对之间若已存在一条轴线共线的同轴心，后续同轴心即为冗余。
   * 只在至少一端属于刚性组时启用，避免影响两个普通单体之间的既有求解语义。
   */
  const redundantMateIds = new Set<string>();
  const keptConcentricAxesByPair = new Map<string, AxisGeometry[]>();
  currentMates.forEach((mate) => {
    if (mate.mateType !== AssemblyMateType.Concentric || internalMateIds.has(mate.mateId)) {
      return;
    }
    const firstComponentId = mate.firstEntity.componentInstanceId;
    const secondComponentId = mate.secondEntity.componentInstanceId;
    const involvesRigidGroup = groupRootByComponentId.has(firstComponentId)
      || groupRootByComponentId.has(secondComponentId);
    if (!involvesRigidGroup || firstComponentId === secondComponentId) {
      return;
    }

    /**
     * 代表轴线必须取“刚性组一侧”的实体轴线：同一对超节点的多条同轴心里，两条 Mate 的 firstEntity 可能分处不同侧，
     * 且驱动/联动后外部零件与刚性组的轴线未必仍共线；只有刚性组内部各孔的轴线始终随刚体一起保持相对共线，
     * 才能稳定判定冗余。
     */
    const groupSideEntity = groupRootByComponentId.has(firstComponentId)
      ? mate.firstEntity
      : mate.secondEntity;
    const mateAxis = resolveAxisGeometry(groupSideEntity);
    if (!mateAxis) {
      return;
    }

    const pairKey = resolveSuperNodePairKey(
      resolveSuperNodeId(firstComponentId),
      resolveSuperNodeId(secondComponentId),
    );
    const keptAxes = keptConcentricAxesByPair.get(pairKey) ?? [];
    // 与同一超节点对已保留的任一条同轴心轴线共线，则本条冗余。
    if (keptAxes.some((keptAxis) => areAxesCoincident(keptAxis, mateAxis, tolerance))) {
      redundantMateIds.add(mate.mateId);
      return;
    }
    keptAxes.push(mateAxis);
    keptConcentricAxesByPair.set(pairKey, keptAxes);
  });

  return {
    groupRootByComponentId,
    membersByGroupRoot,
    internalMateIds,
    redundantMateIds,
  };
}
